import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Typography } from '@material-ui/core';
import ReactPlayer from 'react-player';
import classNames from 'classnames';
import { BASE_URL, IMAGE_PATHS, VIDEO_PATHS } from '../../../../constants/paths.constants';
import { poslumaDarkBlue, black, darkGrey, universalStyles } from '../../../../app/theme';

const useStyles = makeStyles(() => ({
  ...universalStyles,
  headerImage: {
    display: 'block',
    width: '101%',
    marginLeft: -2,
  },
  video: {
    width: '80%',
    aspectRatio: '16/9',
    margin: '25px 10% 6px',
  },
  readHeading: {
    marginBottom: -20,
    color: poslumaDarkBlue,
  },
  heading: {
    paddingTop: 30,
  },
  boneLesionCheckBox: {
    background: 'transparent linear-gradient(270deg, #F0818100 0%, #43C0E8 200%) 0% 0% no-repeat padding-box',
    display: 'flex',
    marginBottom: 15,
    marginLeft: 50,
    '&:first-of-type': {
      marginTop: 20,
    },
  },
  boneLesionText: {
    paddingLeft: 10,
  },
  checkBox: {
    background: 'transparent linear-gradient(270deg, #F0818100 0%, #43C0E8 200%) 0% 0% no-repeat padding-box',
    display: 'flex',
    marginBottom: 15,
    '&:first-of-type': {
      marginTop: 20,
    },
  },
  checkText: {
    color: darkGrey,
    fontSize: 20,
    fontWeight: 600,
    marginTop: 12,
    marginBottom: 12,
    marginLeft: 20,
    width: '85%',
    verticalAlign: 'middle',
  },
  checkTextHeader: {
    color: darkGrey,
    fontSize: 20,
    lineHeight: 1.3,
    fontWeight: 600,
  },
  checklist: {
    marginLeft: '50px',
    fontSize: 18,
    lineHeight: 1.3,
    color: darkGrey,
    '& > li::before': {
      content: '"\u2022"',
      color: black,
    },
  },
  checkImage: {
    width: 39,
    height: 39,
    marginLeft: 30,
    marginTop: 20,
    marginBottom: 20,
  },
  superscript: {
    fontWeight: 400,
    fontSize: 11,
  },
  superscriptHeader: {
    fontWeight: 400,
    fontSize: 13,
  },
}));

const InterpretationPage11 = (): JSX.Element => {
  const classes = useStyles();

  return (
    <>
      <div className={classes.poslumaBox}>
        <img
          className={classes.headerImage}
          src={`${BASE_URL}${IMAGE_PATHS.STEPS[2].INTERPRETATION_STEP_1.path}`}
          alt={`${IMAGE_PATHS.STEPS[2].INTERPRETATION_STEP_1.alt}`}
        />
        <div className={classes.whiteBox}>
          <Typography variant="h2">STEP 1:</Typography>
          <Typography variant="h3">Review General Interpretation Criteria and Read Methodology</Typography>
          <Typography className={classes.heading} variant="h4">
            Read Methodology
          </Typography>
          <ul className={classes.poslumaList}>
            <div className={classes.checkBox}>
              <img
                className={classes.checkImage}
                src={`${BASE_URL}${IMAGE_PATHS.BLUE_CHECKMARK.path}`}
                alt={`${IMAGE_PATHS.BLUE_CHECKMARK.alt}`}
              />
              <li>
                <Typography className={classes.checkText}>
                  Knowledge of the typical sites of prostate cancer spread is vital for assessment of POSLUMA images
                  <sup className={classes.superscriptHeader}>1</sup>
                </Typography>
              </li>
            </div>
            <div className={classes.checkBox}>
              <img
                className={classes.checkImage}
                src={`${BASE_URL}${IMAGE_PATHS.BLUE_CHECKMARK.path}`}
                alt={`${IMAGE_PATHS.BLUE_CHECKMARK.alt}`}
              />
              <div className={classes.checkText}>
                <Typography className={classes.checkTextHeader}>
                  The most common sites of recurrence are local recurrence and lymph node metastases
                  <sup className={classes.superscriptHeader}>1,2</sup>
                </Typography>
                <ul className={classNames(classes.poslumaList, classes.checklist)}>
                  <li>
                    <span>
                      <strong>
                        Recurrence is often initially regional, followed by distant lymph nodes
                        <sup className={classes.superscript}>2</sup>
                      </strong>
                    </span>
                  </li>
                  <li>
                    <span>
                      <strong>
                        At later stages, the skeleton and soft tissue organs may be affected
                        <sup className={classes.superscript}>3</sup>
                      </strong>
                    </span>
                  </li>
                  <li>
                    <span>
                      <strong>
                        Following radical prostatectomy, the most likely sites of local recurrence are at the
                        vesicourethral anastomosis, urinary bladder neck, and rectovesical space
                        <sup className={classes.superscript}>4</sup>
                      </strong>
                    </span>
                  </li>
                </ul>
              </div>
            </div>
            <div className={classes.checkBox}>
              <img
                className={classes.checkImage}
                src={`${BASE_URL}${IMAGE_PATHS.BLUE_CHECKMARK.path}`}
                alt={`${IMAGE_PATHS.BLUE_CHECKMARK.alt}`}
              />
              <li>
                <Typography className={classes.checkText}>
                  Following external beam radiotherapy, local recurrence is commonly identified at the site of the
                  primary tumor
                  <sup className={classes.superscript}>5</sup>
                </Typography>
              </li>
            </div>
          </ul>
          <Typography variant="h4">Interpretation Methodology - Read Methodology</Typography>
          <ul className={classes.poslumaList}>
            <li>
              <span>
                POSLUMA binds to prostate-specific membrane antigen (PSMA). PET images obtained using POSLUMA indicate
                the presence of PSMA in tissues
                <sup className={classes.superscript}>6</sup>
              </span>
            </li>
            <li>
              <span>
                Interpretation of POSLUMA is qualitative
                <sup className={classes.superscript}>1</sup>
              </span>
            </li>
            <ul className={classes.poslumaNestedList}>
              <li>
                <span>
                  Lesions should be considered suspicious if uptake is greater than physiologic uptake in that tissue or
                  greater than adjacent background if no physiologic uptake is expected
                  <sup className={classes.superscript}>6</sup>
                </span>
              </li>
              <li>
                <span>
                  Tumors that do not express PSMA will not be visualized
                  <sup className={classes.superscript}>6</sup>
                </span>
              </li>
            </ul>
            <li>
              <span>
                The PET images must not be read in isolation; reference must be made to the co-registered anatomical
                image (e.g., computed tomography [CT])
                <sup className={classes.superscript}>1</sup>
              </span>
            </li>
            <div className={classes.boneLesionCheckBox}>
              <span className={classes.boneLesionText}>
                <i>
                  <strong>This is especially important for bone lesions</strong>
                </i>
              </span>
            </div>
            <li>
              <span>
                Recommended standardized uptake values (SUV) scales are provided throughout the training
                <sup className={classes.superscript}>1</sup>
              </span>
            </li>
            <ul className={classes.poslumaNestedList}>
              <li>These scales are not mandated, and readers may adjust as required</li>
            </ul>
            <li>
              <span>
                Assessment is undertaken in an “inside-to-outside” (tumor, nodes, metastases) direction
                <sup className={classes.superscript}>1</sup>
              </span>
            </li>
            <ul className={classes.poslumaNestedList}>
              <li>Prostate/prostate bed</li>
              <li>Pelvic lymph nodes</li>
              <li>Abdomen</li>
              <li>Thorax</li>
              <li>Skeleton</li>
            </ul>
          </ul>
          <Typography variant="h4">General Appearance</Typography>
          <Typography variant="body1">
            <strong>Assessment Procedures</strong>
            <sup className={classes.superscript}>1</sup>
            <strong>:</strong>
          </Typography>
          <ul className={classes.poslumaList}>
            <li>
              Review the maximum intensity projection (MIP) for an overview of possible abnormally increased uptake
            </li>
            <ul className={classes.poslumaNestedList}>
              <li>The suggested SUV scale is 0-10</li>
            </ul>
            <li>Check for signs of extravasation</li>
          </ul>
          <div className={classes.video}>
            <ReactPlayer className={classes.responsiveVideo} url={VIDEO_PATHS.INTERPRETATION_AVI.path} controls />
          </div>
        </div>
      </div>
    </>
  );
};

export default InterpretationPage11;
